import * as THREE from 'three';
import { Materials } from '../render/Materials.js';
import { lerp, smoothstep } from '../util/math.js';

/**
 * =============================================================================
 * CONTRATO DE MAPA — leia isto antes de escrever um mapa
 * =============================================================================
 *
 * Um mapa é um módulo em `src/world/maps/<id>.js` que faz `export default` de um
 * objeto com a forma abaixo. Ele NÃO importa three, NÃO cria material, NÃO toca
 * na cena e NÃO guarda estado de módulo: só descreve e chama primitivas do kit.
 * Quem materializa é o `Level`.
 *
 * ```js
 * export default {
 *   id: 'blackout',                 // único, é o que vai em ?map=<id>
 *   nome: 'Blackout',               // aparece no menu (pt-BR)
 *   regiao: 'Complexo árido',       // subtítulo no menu
 *   seed: 20260813,                 // semente do rng; mesmo seed = mesmo mapa
 *
 *   bounds: { min: [-34, -1, -34], max: [34, 20, 34] },   // arena inteira
 *
 *   player: { spawn: [0, 1.68, 29.5], yaw: 0 },   // spawn é ALTURA DO OLHO
 *
 *   // Tabela de spawn de IA. y é o PÉ. `grounded:false` = telhado/passarela,
 *   // que a navgrid não cobre (ver limitação de um piso por célula em Level).
 *   // kind: 'enemy' | 'sniper' | 'patrol'. Só 'enemy'/'sniper' viram enemySpawns.
 *   spawns: [ [x, y, z, yaw, kind, grounded], ... ],
 *
 *   // Luzes locais de interior. [x, y, z, cor, intensidade, alcance, sombra]
 *   // Sombra só liga de verdade em quality 'ultra'. Orçamento: 6 luzes.
 *   luzes: [ [-16.5, 2.85, -19, 0xffc08a, 26, 15, true], ... ],
 *   // Índices (dentro de `luzes`) que piscam. O resto fica firme.
 *   piscam: [0, 4],
 *
 *   // --- BLOCO DE AMBIENTE ---------------------------------------------------
 *   // Lido por Lighting.js. Tudo opcional: o que faltar cai no padrão árido.
 *   ambiente: {
 *     sol:  { azimute: 118, elevacao: 48, intensidade: 11, cor: 0xfff2da },
 *     ceu:  { zenite: 0x4a86c8, horizonte: 0xcfd0c4, chao: 0x6d6250,
 *             corSol: 0xfff4e0, turbidez: 1.2, neblina: 0.34 },
 *     hemi: { ceu: 0x8fb2d8, chao: 0x7d6b4e, intensidade: 2.6 },
 *     fog:  { cor: 0xc6b899, densidade: 0.0034 },
 *     // Paleta de superfícies: nomes de Materials.names. Serve para o mapa
 *     // (e para um futuro gerador) trocar o vocabulário de material sem
 *     // reescrever a geometria. Chaves livres, estas são as convencionadas:
 *     paleta: { terreno: 'sand', piso: 'asphalt', parede: 'plaster',
 *               estrutura: 'concrete', metal: 'metalPainted', ferro: 'gunMetal',
 *               madeira: 'woodPlank', ferrugem: 'rust', pano: 'fabric' },
 *   },
 *
 *   build(kit, ctx) { ... },   // constrói o mapa. Ver ordem de fases abaixo.
 * };
 * ```
 *
 * ---------------------------------------------------------------------------
 * ORDEM DAS FASES DENTRO DE build(kit, ctx) — não é decorativa
 * ---------------------------------------------------------------------------
 * 1. GEOMETRIA  — chão, muros, prédios, cobertura, adereço fixo. Tudo que é
 *                 `kit.solid()` entra aqui, porque as fases seguintes consultam
 *                 `kit.isSolid()` e `kit.groundAt()` sobre a lista já completa.
 * 2. ESPALHAMENTO — `kit.crates/pallets/drums/tyres/rubbleField/...`. Precisa da
 *                 lista de colisores fechada e ACRESCENTA colisores no fim dela.
 * Chamar na ordem errada não quebra, mas muda o sorteio (o rng é um fluxo só) e
 * põe caixa dentro de parede.
 *
 * O `ctx` recebido é `{ rng, seed, quality, engine }`. `rng` é o MESMO fluxo do
 * kit: qualquer sorteio a mais, em qualquer ponto, desloca todo o resto do mapa.
 *
 * ---------------------------------------------------------------------------
 * O QUE O KIT OFERECE
 * ---------------------------------------------------------------------------
 * Base:        batch, mat, deco, solid, colliderBox, cylinder, cone, geo,
 *              meshFor, instance, addLight, groundAt, isSolid, groundPlane
 * Construção:  wall (com vãos de porta e janela), building (N andares),
 *              stairs, slab, roof (laje + parapeito), parapet, rail, catwalk,
 *              fence (com portão), container
 * Adereço:     crateStack, coverBlock, planter, rack, stall, leanSheet, tank,
 *              vent, acUnit, emplacement + sandbags, barrel/drums, pole (poste
 *              com fio), cable, mast, dish, awning (toldo), cloth, laundry,
 *              wreck (veículo destruído), rubbleField/bricks/litter (escombro),
 *              crates, pallets, tyres
 *
 * REGRA DE OURO DO KIT: toda primitiva que é sólida emite malha E colisor no
 * mesmo lugar, na mesma chamada. Não existe caminho para desenhar sem colidir
 * (ou vice-versa) por engano — foi assim que o mapa antigo ganhou parede
 * invisível. Quando você quer só enfeite, o nome diz: `deco`.
 *
 * ---------------------------------------------------------------------------
 * O QUE O LEVEL FAZ COM O KIT DEPOIS DO build()
 * ---------------------------------------------------------------------------
 * O kit não toca na cena. Terminado o `build()`, o Level drena, NESTA ORDEM:
 *   `loose` (malhas avulsas, incluindo o chão) → `batches` (uma malha por
 *   superfície) → `instances` (InstancedMesh) → `buildFenceMesh()`.
 * A ordem importa porque é a ordem em que as malhas entram no grupo e em
 * `hitMeshes`; mudá-la muda a assinatura que `tools/dump-level.mjs` compara.
 * Colisores e superfícies já saem prontos em `kit.colliders` / `kit.surfaces`.
 */

const WHITE = [1, 1, 1];
const EMPTY = {};

// tabela de faces: [normal, eixo u, eixo v] com u x v = n, para o winding sair CCW
const BOX_FACES = [
  [[1, 0, 0], [0, 0, -1], [0, 1, 0]],
  [[-1, 0, 0], [0, 0, 1], [0, 1, 0]],
  [[0, 1, 0], [1, 0, 0], [0, 0, -1]],
  [[0, -1, 0], [1, 0, 0], [0, 0, 1]],
  [[0, 0, 1], [1, 0, 0], [0, 1, 0]],
  [[0, 0, -1], [-1, 0, 0], [0, 1, 0]],
];

const _v = new THREE.Vector3();
const _n = new THREE.Vector3();
const _nm = new THREE.Matrix3();

/**
 * Acumulador de geometria. Posições em espaço de mundo, UV em metros e cor de
 * vértice carregando o tint da caixa multiplicado por uma rampa de sujeira por
 * altura, aplicada só a faces quase verticais — encardido sobe pela parede, não
 * fica no chão, e fazer isso aqui não custa nada em tempo de execução.
 */
export class Batch {
  constructor() {
    this.pos = []; this.nor = []; this.uv = []; this.col = []; this.idx = [];
    this.n = 0;
  }

  vert(x, y, z, nx, ny, nz, u, v, r, g, b) {
    this.pos.push(x, y, z);
    this.nor.push(nx, ny, nz);
    this.uv.push(u, v);
    const k = Math.abs(ny) < 0.55 ? 0.5 + 0.5 * smoothstep(y / 1.3) : 1;
    this.col.push(r * k, g * k, b * k);
    return this.n++;
  }

  addBox(cx, cy, cz, sx, sy, sz, opt = EMPTY) {
    const d = opt.density ?? 0.5;
    const t = opt.tint || WHITE;
    const rot = opt.rotY || 0;
    const cr = Math.cos(rot), sr = Math.sin(rot);
    const uo = opt.uoff || 0, vo = opt.voff || 0;
    const hx = sx * 0.5, hy = sy * 0.5, hz = sz * 0.5;
    for (let f = 0; f < 6; f++) {
      if (opt.faces !== undefined && !(opt.faces & (1 << f))) continue;
      const F = BOX_FACES[f], nb = F[0], U = F[1], V = F[2];
      const hu = Math.abs(U[0]) * hx + Math.abs(U[1]) * hy + Math.abs(U[2]) * hz;
      const hv = Math.abs(V[0]) * hx + Math.abs(V[1]) * hy + Math.abs(V[2]) * hz;
      const hn = Math.abs(nb[0]) * hx + Math.abs(nb[1]) * hy + Math.abs(nb[2]) * hz;
      const us = hu * 2 * d, vs = hv * 2 * d;
      const nx = nb[0] * cr + nb[2] * sr, ny = nb[1], nz = -nb[0] * sr + nb[2] * cr;
      const base = this.n;
      for (let k = 0; k < 4; k++) {
        const su = (k === 0 || k === 3) ? -1 : 1;
        const sv = k < 2 ? -1 : 1;
        const lx = nb[0] * hn + U[0] * hu * su + V[0] * hv * sv;
        const ly = nb[1] * hn + U[1] * hu * su + V[1] * hv * sv;
        const lz = nb[2] * hn + U[2] * hu * su + V[2] * hv * sv;
        this.vert(
          cx + lx * cr + lz * sr, cy + ly, cz - lx * sr + lz * cr,
          nx, ny, nz,
          uo + (su < 0 ? 0 : us), vo + (sv < 0 ? 0 : vs),
          t[0], t[1], t[2],
        );
      }
      this.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
  }

  /** Quad a partir de quatro cantos em ordem de winding; UV vem do comprimento das arestas. */
  addQuad(a, b, c, d, opt = EMPTY) {
    const den = opt.density ?? 0.5;
    const t = opt.tint || WHITE;
    _v.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
    _n.set(d[0] - a[0], d[1] - a[1], d[2] - a[2]).cross(_v).normalize();
    const wu = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]) * den;
    const wv = Math.hypot(d[0] - a[0], d[1] - a[1], d[2] - a[2]) * den;
    const base = this.n;
    const pts = [a, b, c, d];
    const uvs = [[0, 0], [wu, 0], [wu, wv], [0, wv]];
    for (let i = 0; i < 4; i++) {
      this.vert(pts[i][0], pts[i][1], pts[i][2], -_n.x, -_n.y, -_n.z, uvs[i][0], uvs[i][1], t[0], t[1], t[2]);
    }
    this.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  addGeometry(geo, mat4, opt = EMPTY) {
    const p = geo.getAttribute('position');
    const na = geo.getAttribute('normal');
    const ua = geo.getAttribute('uv');
    if (!p) return;
    const t = opt.tint || WHITE;
    const us = opt.uvScale ?? 1, vs = opt.vScale ?? us;
    _nm.getNormalMatrix(mat4);
    const base = this.n;
    for (let i = 0; i < p.count; i++) {
      _v.fromBufferAttribute(p, i).applyMatrix4(mat4);
      if (na) _n.fromBufferAttribute(na, i).applyMatrix3(_nm).normalize();
      else _n.set(0, 1, 0);
      this.vert(
        _v.x, _v.y, _v.z, _n.x, _n.y, _n.z,
        ua ? ua.getX(i) * us : 0, ua ? ua.getY(i) * vs : 0,
        t[0], t[1], t[2],
      );
    }
    const idx = geo.getIndex();
    if (idx) for (let i = 0; i < idx.count; i++) this.idx.push(base + idx.getX(i));
    else for (let i = 0; i < p.count; i++) this.idx.push(base + i);
  }

  geometry() {
    const g = new THREE.BufferGeometry();
    const uv = new THREE.Float32BufferAttribute(this.uv, 2);
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute('uv', uv);
    g.setAttribute('uv1', uv); // aoMap lê o canal 1 se algum dia uma textura pedir
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setIndex(this.n > 65535
      ? new THREE.Uint32BufferAttribute(this.idx, 1)
      : new THREE.Uint16BufferAttribute(this.idx, 1));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }

  get triangles() { return this.idx.length / 3; }
}

/**
 * O kit. Uma instância por construção de mapa: ele É o acumulador que as
 * primitivas recebem. Nada aqui é estado de módulo, então dois mapas podem ser
 * construídos na mesma página sem contaminar um ao outro.
 */
export class MapKit {
  constructor({ rng, quality = 'high', seed = 0 } = {}) {
    this.rng = rng || Math.random;
    this.quality = quality;
    this.seed = seed;

    this.batches = new Map();   // superfície -> Batch
    this.mats = new Map();      // chave -> material (Level assume a posse ao final)
    this.colliders = [];        // Box3[] em espaço de mundo
    this.surfaces = [];         // nome de superfície, paralelo a colliders

    this.instances = [];        // pedidos de InstancedMesh, materializados no flush
    this.loose = [];            // malhas avulsas: { mesh, sway }
    this.fencePanels = [];      // painéis de tela, viram uma malha só no flush
    this.emplacements = [];     // parapeitos de sacaria pendentes
  }

  // ---------------------------------------------------------------------------
  // base
  // ---------------------------------------------------------------------------

  /** Material compartilhado por superfície, com cor de vértice ligada. */
  mat(surface) {
    let m = this.mats.get(surface);
    if (!m) {
      m = Materials.get(surface, { repeat: 1, size: 512, clone: true, overrides: { vertexColors: true } });
      m.name = `level:${surface}`;
      this.mats.set(surface, m);
    }
    return m;
  }

  batch(surface) {
    let b = this.batches.get(surface);
    if (!b) { b = new Batch(); b.surface = surface; this.batches.set(surface, b); }
    return b;
  }

  /** Caixa só visual. Nunca colide. */
  deco(surface, cx, cy, cz, sx, sy, sz, opt) {
    this.batch(surface).addBox(cx, cy, cz, sx, sy, sz, opt || EMPTY);
  }

  /** Caixa desenhada E sólida. Caixa girada recebe o AABB da rotação. */
  solid(surface, cx, cy, cz, sx, sy, sz, opt) {
    if (sx <= 0.001 || sy <= 0.001 || sz <= 0.001) return;
    this.batch(surface).addBox(cx, cy, cz, sx, sy, sz, opt || EMPTY);
    let ex = sx * 0.5, ez = sz * 0.5;
    const rot = opt && opt.rotY;
    if (rot) {
      const c = Math.abs(Math.cos(rot)), s = Math.abs(Math.sin(rot));
      ex = (sx * c + sz * s) * 0.5;
      ez = (sx * s + sz * c) * 0.5;
    }
    this.colliders.push(new THREE.Box3(
      new THREE.Vector3(cx - ex, cy - sy * 0.5, cz - ez),
      new THREE.Vector3(cx + ex, cy + sy * 0.5, cz + ez),
    ));
    this.surfaces.push(surface);
  }

  /** Colisor cru, para geometria que não é caixa (cilindro, casco, pilha). */
  colliderBox(minX, minY, minZ, maxX, maxY, maxZ, surface) {
    this.colliders.push(new THREE.Box3(
      new THREE.Vector3(minX, minY, minZ), new THREE.Vector3(maxX, maxY, maxZ)));
    this.surfaces.push(surface);
  }

  /** Despeja uma BufferGeometry qualquer dentro do lote de uma superfície. */
  geo(surface, geometry, matrix, opt) {
    this.batch(surface).addGeometry(geometry, matrix || new THREE.Matrix4(), opt || EMPTY);
  }

  /**
   * Cilindro (tanque, tambor, tubo, pilar redondo). `y` é o CENTRO.
   * `solid:true` acrescenta o AABB do cilindro como colisor — é o certo para
   * um tanque de água: a cápsula do jogador não sente o chanfro de 15 cm que
   * um colisor cilíndrico daria, e um Box3 custa a metade do teste.
   */
  cylinder(surface, x, y, z, radius, height, opt = EMPTY) {
    const geo = new THREE.CylinderGeometry(opt.rTop ?? radius, radius, height, opt.seg ?? 14, 1);
    this.geo(surface, geo, new THREE.Matrix4().makeTranslation(x, y, z),
      { tint: opt.tint, uvScale: opt.uvScale ?? 2 });
    geo.dispose();
    if (opt.solid) {
      const r = Math.max(radius, opt.rTop ?? radius);
      this.colliderBox(x - r, y - height * 0.5, z - r, x + r, y + height * 0.5, z + r, surface);
    }
  }

  /** Cone (chapéu de exaustor, funil). `y` é o CENTRO. */
  cone(surface, x, y, z, radius, height, opt = EMPTY) {
    const geo = new THREE.ConeGeometry(radius, height, opt.seg ?? 10);
    this.geo(surface, geo, new THREE.Matrix4().makeTranslation(x, y, z),
      { tint: opt.tint, uvScale: opt.uvScale ?? 2 });
    geo.dispose();
  }

  /** Malha avulsa (não entra no lote). Use quando ela precisa se mover. */
  meshFor(geometry, material, { position, surface, sway } = {}) {
    const mesh = new THREE.Mesh(geometry, material);
    if (position) mesh.position.set(position[0], position[1], position[2]);
    mesh.castShadow = true;
    if (surface) mesh.userData.surface = surface;
    this.loose.push({ mesh, sway: sway || null });
    return mesh;
  }

  /** Pedido de InstancedMesh. Linhas são [x, y, z, yaw, escala]. */
  instance(geometry, surface, rows, name, tint = null) {
    if (!rows || !rows.length) { geometry.dispose?.(); return; }
    this.instances.push({ geometry, surface, rows, name, tint });
  }

  /** Definição de luz local, repassada ao Level. */
  addLight(def) { (this.lights || (this.lights = [])).push(def); }

  // Atalhos para o mapa não precisar importar util/math.js. Um mapa deve poder
  // ser lido de cima a baixo sem abrir outro arquivo.
  lerp(a, b, t) { return a + (b - a) * t; }
  /** Sorteio uniforme. CONSOME o rng — cuidado com a ordem (ver contrato). */
  rand(lo, hi) { return lo + (hi - lo) * this.rng(); }

  /** Topo sólido mais alto sob (x, z) em que um agente poderia estar de pé. */
  groundAt(x, z, maxY = 2.9) {
    let best = 0;
    for (let i = 0; i < this.colliders.length; i++) {
      const b = this.colliders[i];
      if (x < b.min.x || x > b.max.x || z < b.min.z || z > b.max.z) continue;
      if (b.max.y > best && b.max.y <= maxY) best = b.max.y;
    }
    return best;
  }

  /** Teste barato de ponto dentro de sólido. */
  isSolid(point, pad = 0) {
    for (let i = 0; i < this.colliders.length; i++) {
      const b = this.colliders[i];
      if (point.x > b.min.x - pad && point.x < b.max.x + pad &&
          point.y > b.min.y - pad && point.y < b.max.y + pad &&
          point.z > b.min.z - pad && point.z < b.max.z + pad) return true;
    }
    return false;
  }

  /**
   * Plano de chão. Não gera colisor: o piso do jogador é y=0 por definição e
   * um Box3 de 180 m só atrapalharia a navgrid.
   */
  groundPlane(surface, { size = 180, repeat = 45, x = 0, y = 0, z = 0, name } = {}) {
    const geo = new THREE.PlaneGeometry(size, size, 1, 1);
    geo.rotateX(-Math.PI / 2);
    geo.setAttribute('uv1', geo.getAttribute('uv'));
    const mesh = new THREE.Mesh(geo, Materials.get(surface, { repeat, size: 512 }));
    mesh.position.set(x, y, z);
    mesh.receiveShadow = true;
    mesh.name = name || surface;
    mesh.userData.surface = surface;
    this.loose.push({ mesh, sway: null, ground: true });
    return mesh;
  }

  // ---------------------------------------------------------------------------
  // construção
  // ---------------------------------------------------------------------------

  /**
   * Trecho de parede com vãos retangulares abertos — porta é vão que chega ao
   * chão, janela é vão que começa acima dele. Os vãos são dados em coordenada
   * de MUNDO sobre o eixo da parede, o que deixa a chamada legível e impossível
   * de errar por pouco.
   *
   * O vão não é subtração booleana: a parede emite as caixas EM VOLTA do buraco,
   * então o buraco é buraco de verdade para bala, linha de visão da IA e cápsula
   * do jogador, de graça.
   *
   * opt: { tint, density, openings:[{u0,u1,y0,y1,frame}], plinth, trim, ribs }
   */
  wall(surface, ax, az, bx, bz, y0, y1, t, opt = EMPTY) {
    const horiz = Math.abs(bx - ax) >= Math.abs(bz - az);
    const s0 = Math.min(horiz ? ax : az, horiz ? bx : bz);
    const s1 = Math.max(horiz ? ax : az, horiz ? bx : bz);
    const fixed = horiz ? az : ax;
    const tint = opt.tint;
    const density = opt.density;
    const plinth = opt.plinth !== false;
    const trim = opt.trim ?? 0;
    const ribs = opt.ribs || 0;

    const seg = (s, e, ya, yb) => {
      if (e - s < 0.02 || yb - ya < 0.02) return;
      const cu = (s + e) * 0.5, cy = (ya + yb) * 0.5;
      const lu = e - s, ly = yb - ya;
      if (horiz) this.solid(surface, cu, cy, fixed, lu, ly, t, { tint, density, uoff: s * 0.5 });
      else this.solid(surface, fixed, cy, cu, t, ly, lu, { tint, density, voff: s * 0.5 });

      // Faixa de umidade/sujeira na base: material mais grosseiro onde o reboco
      // saiu. Saliente 4 cm — pouco demais para a cápsula sentir.
      if (plinth && ya <= y0 + 0.001 && yb > y0 + 0.7) {
        const ph = 0.62;
        if (horiz) this.deco('concrete', cu, y0 + ph * 0.5, fixed, lu, ph, t + 0.08, { tint: [0.78, 0.76, 0.72], density: 0.8 });
        else this.deco('concrete', fixed, y0 + ph * 0.5, cu, t + 0.08, ph, lu, { tint: [0.78, 0.76, 0.72], density: 0.8 });
      }
      if (ribs > 0) {
        for (let u = s + ribs * 0.5; u < e - 0.05; u += ribs) {
          if (horiz) this.deco(surface, u, cy, fixed, 0.14, ly - 0.02, t + 0.1, { tint, density: 1.4 });
          else this.deco(surface, fixed, cy, u, t + 0.1, ly - 0.02, 0.14, { tint, density: 1.4 });
        }
      }
    };

    const ops = (opt.openings || []).filter((o) => o.u1 > s0 && o.u0 < s1).sort((p, q) => p.u0 - q.u0);
    let cur = s0;
    for (const o of ops) {
      const a0 = Math.max(s0, o.u0), a1 = Math.min(s1, o.u1);
      seg(cur, a0, y0, y1);
      const oy0 = Math.max(y0, o.y0), oy1 = Math.min(y1, o.y1);
      if (oy0 > y0 + 0.01) seg(a0, a1, y0, oy0);
      if (oy1 < y1 - 0.01) seg(a0, a1, oy1, y1);
      if (o.frame !== false) this.reveal(surface, horiz, fixed, a0, a1, oy0, oy1, t, tint);
      cur = Math.max(cur, a1);
    }
    seg(cur, s1, y0, y1);

    if (trim > 0) {
      const cu = (s0 + s1) * 0.5, lu = s1 - s0;
      if (horiz) this.deco('concrete', cu, y1 - trim * 0.5, fixed, lu, trim, t + 0.16, { tint: [0.9, 0.88, 0.84], density: 0.9 });
      else this.deco('concrete', fixed, y1 - trim * 0.5, cu, t + 0.16, trim, lu, { tint: [0.9, 0.88, 0.84], density: 0.9 });
    }
  }

  /** Ombreira, verga e peitoril em volta de um vão — é isso que dá espessura à parede. */
  reveal(surface, horiz, fixed, u0, u1, y0, y1, t, tint) {
    const jw = 0.15, jt = t + 0.14;
    const cy = (y0 + y1) * 0.5, hy = y1 - y0;
    const opts = { tint: tint || [0.94, 0.92, 0.88], density: 1.1 };
    if (horiz) {
      this.deco(surface, u0 + jw * 0.5, cy, fixed, jw, hy, jt, opts);
      this.deco(surface, u1 - jw * 0.5, cy, fixed, jw, hy, jt, opts);
      this.deco(surface, (u0 + u1) * 0.5, y1 - 0.09, fixed, u1 - u0, 0.18, jt, opts);
      if (y0 > 0.4) this.deco('concrete', (u0 + u1) * 0.5, y0 - 0.05, fixed, u1 - u0 + 0.3, 0.12, t + 0.34, { tint: [0.85, 0.83, 0.79], density: 1.1 });
    } else {
      this.deco(surface, fixed, cy, u0 + jw * 0.5, jt, hy, jw, opts);
      this.deco(surface, fixed, cy, u1 - jw * 0.5, jt, hy, jw, opts);
      this.deco(surface, fixed, y1 - 0.09, (u0 + u1) * 0.5, jt, 0.18, u1 - u0, opts);
      if (y0 > 0.4) this.deco('concrete', fixed, y0 - 0.05, (u0 + u1) * 0.5, t + 0.34, 0.12, u1 - u0 + 0.3, { tint: [0.85, 0.83, 0.79], density: 1.1 });
    }
  }

  /**
   * Lance de escada. Cada degrau é uma caixa de altura cheia a partir de `baseY`,
   * então não existe vão embaixo para cair dentro e o step-up de 0,42 m do
   * jogador sobe sem pulo.
   *
   * ATENÇÃO ao dimensionar: `rise` acima de 0,42 vira parede intransponível.
   * `tools/test-mapkit.mjs` reprova isso.
   */
  stairs(surface, x, z, dirX, dirZ, steps, rise, run, width, baseY = 0, opt = EMPTY) {
    const halfW = width * 0.5;
    for (let i = 0; i < steps; i++) {
      const cx = x + dirX * (i + 0.5) * run;
      const cz = z + dirZ * (i + 0.5) * run;
      const top = baseY + (i + 1) * rise;
      const sx = dirX ? run : width, sz = dirZ ? run : width;
      this.solid(surface, cx, (baseY + top) * 0.5, cz, sx, top - baseY, sz, { tint: opt.tint, density: 0.9 });
    }
    // as vigas laterais fazem a escada ler como concreto moldado, não degrau flutuante
    const len = steps * run;
    const midX = x + dirX * len * 0.5, midZ = z + dirZ * len * 0.5;
    const topY = baseY + steps * rise;
    if (opt.stringer !== false) {
      const sw = 0.16;
      for (const s of [-1, 1]) {
        const ox = dirZ ? s * (halfW + sw * 0.5) : 0;
        const oz = dirX ? s * (halfW + sw * 0.5) : 0;
        this.deco(surface, midX + ox, baseY + (topY - baseY) * 0.5, midZ + oz,
          dirX ? len : sw, topY - baseY, dirZ ? len : sw, { tint: [0.86, 0.85, 0.82], density: 0.7 });
      }
    }
    if (opt.rail) {
      const rh = 1.02;
      for (const s of (opt.rail === 'both' ? [-1, 1] : [opt.rail === 'left' ? -1 : 1])) {
        const ox = dirZ ? s * (halfW + 0.06) : 0;
        const oz = dirX ? s * (halfW + 0.06) : 0;
        // um sólido fino para todo o guarda-corpo; balaústre e montante são enfeite
        for (let i = 0; i < steps; i += 2) {
          const px = x + dirX * (i + 1) * run + ox, pz = z + dirZ * (i + 1) * run + oz;
          const py = baseY + (i + 1) * rise;
          this.deco('gunMetal', px, py + rh * 0.5, pz, 0.06, rh, 0.06, { density: 2 });
        }
        this.solid('gunMetal', midX + ox, baseY + (topY - baseY) * 0.5 + rh, midZ + oz,
          dirX ? len : 0.07, 0.09, dirZ ? len : 0.07, { density: 2, rotY: 0 });
      }
    }
    return topY;
  }

  /** Laje de piso/telhado. `holes` são retângulos [x0,x1,z0,z1] mantidos abertos. */
  slab(surface, x0, x1, z0, z1, top, thick, holes = [], opt = EMPTY) {
    const cy = top - thick * 0.5;
    const pieces = [[x0, x1, z0, z1]];
    for (const h of holes) {
      const next = [];
      for (const p of pieces) {
        const [px0, px1, pz0, pz1] = p;
        if (h[0] >= px1 || h[1] <= px0 || h[2] >= pz1 || h[3] <= pz0) { next.push(p); continue; }
        if (h[2] > pz0) next.push([px0, px1, pz0, h[2]]);
        if (h[3] < pz1) next.push([px0, px1, h[3], pz1]);
        const za = Math.max(pz0, h[2]), zb = Math.min(pz1, h[3]);
        if (h[0] > px0) next.push([px0, h[0], za, zb]);
        if (h[1] < px1) next.push([h[1], px1, za, zb]);
      }
      pieces.length = 0; pieces.push(...next);
    }
    for (const [a, b, c, d] of pieces) {
      if (b - a < 0.02 || d - c < 0.02) continue;
      this.solid(surface, (a + b) * 0.5, cy, (c + d) * 0.5, b - a, thick, d - c,
        { tint: opt.tint, density: opt.density ?? 0.5 });
    }
  }

  /** Parapeito em volta de um telhado, com brechas abertas como posição de tiro. */
  parapet(surface, x0, x1, z0, z1, base, h, t, gaps = []) {
    const open = (side) => gaps.filter((g) => g[0] === side).map((g) => ({ u0: g[1], u1: g[2], y0: base, y1: base + h, frame: false }));
    this.wall(surface, x0, z0, x1, z0, base, base + h, t, { openings: open('n'), plinth: false, trim: 0.1 });
    this.wall(surface, x0, z1, x1, z1, base, base + h, t, { openings: open('s'), plinth: false, trim: 0.1 });
    this.wall(surface, x0, z0 + t, x0, z1 - t, base, base + h, t, { openings: open('w'), plinth: false, trim: 0.1 });
    this.wall(surface, x1, z0 + t, x1, z1 - t, base, base + h, t, { openings: open('e'), plinth: false, trim: 0.1 });
  }

  /**
   * Telhado completo: laje + parapeito + testeira (a barra que revela a
   * espessura do beiral visto de baixo). Devolve a altura de pisada do telhado.
   *
   * opt: { thick, holes, tint, overhang, parapeito:{h,t,gaps,surface}, fascia:'n'|'s'|'both' }
   */
  roof(surface, x0, x1, z0, z1, top, opt = EMPTY) {
    const thick = opt.thick ?? 0.35;
    const over = opt.overhang ?? 0;
    this.slab(surface, x0 - over, x1 + over, z0 - over, z1 + over, top, thick, opt.holes || [], { tint: opt.tint });
    if (over > 0.01 && opt.fascia !== false) {
      const cx = (x0 + x1) * 0.5, lx = (x1 - x0) + over * 2;
      for (const z of [z0 - over + 0.05, z1 + over - 0.05]) {
        this.deco('concrete', cx, top - thick - 0.1, z, lx, 0.2, 0.3, { tint: [0.82, 0.81, 0.78], density: 0.9 });
      }
    }
    const par = opt.parapeito;
    if (par) {
      this.parapet(par.surface || surface, x0 - (par.over ?? 0), x1 + (par.over ?? 0),
        z0 - (par.over ?? 0), z1 + (par.over ?? 0), top, par.h ?? 0.9, par.t ?? 0.3, par.gaps || []);
    }
    return top;
  }

  /** Guarda-corpo honesto quanto à colisão: um sólido fino + enfeite. */
  rail(x0, z0, x1, z1, y, h = 1.05) {
    const horiz = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    const len = Math.hypot(x1 - x0, z1 - z0);
    const cx = (x0 + x1) * 0.5, cz = (z0 + z1) * 0.5;
    this.solid('gunMetal', cx, y + h - 0.05, cz, horiz ? len : 0.07, 0.1, horiz ? 0.07 : len, { density: 2 });
    this.deco('gunMetal', cx, y + h * 0.55, cz, horiz ? len : 0.05, 0.06, horiz ? 0.05 : len, { density: 2 });
    const n = Math.max(2, Math.round(len / 1.4));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      this.deco('gunMetal', lerp(x0, x1, t), y + h * 0.5, lerp(z0, z1, t), 0.07, h, 0.07, { density: 2 });
    }
  }

  /**
   * Passarela suspensa: laje fina + guarda-corpo nos lados pedidos.
   * `rails` é uma lista de 'n' | 's' | 'e' | 'w' (n = z menor, w = x menor).
   */
  catwalk(surface, x0, x1, z0, z1, y, opt = EMPTY) {
    const thick = opt.thick ?? 0.22;
    this.slab(surface, x0, x1, z0, z1, y, thick, [], { tint: opt.tint || [0.85, 0.88, 0.9], density: opt.density ?? 0.9 });
    for (const side of (opt.rails || [])) {
      if (side === 'n') this.rail(x0, z0, x1, z0, y);
      else if (side === 's') this.rail(x0, z1, x1, z1, y);
      else if (side === 'w') this.rail(x0, z0, x0, z1, y);
      else if (side === 'e') this.rail(x1, z0, x1, z1, y);
    }
    return y;
  }

  /**
   * Prédio de N andares em uma chamada: quatro fachadas com vãos, lajes de piso,
   * telhado com parapeito. É a primitiva que evita reescrever cem linhas por
   * prédio; para algo fora do padrão, componha wall/slab/roof direto.
   *
   * Vãos: lista de { lado:'n'|'s'|'e'|'w', andar:0..N-1, u0, u1, y0, y1, frame }
   * com u0/u1 em coordenada de MUNDO sobre o eixo do lado e y0/y1 RELATIVOS à
   * base daquele andar (y0:0 = porta, y0:1.05 = janela).
   *
   * Devolve { floorY:[...], roofY, topY } para encaixar escada e adereço.
   */
  building(o) {
    const surface = o.surface || 'plaster';
    const { x0, x1, z0, z1 } = o;
    const t = o.t ?? 0.4;
    const floors = o.floors ?? 1;
    const fh = o.floorHeight ?? 3.3;
    const base = o.base ?? 0;
    const tint = o.tint;
    const trim = o.trim ?? 0.2;
    const wallH = base + floors * fh;

    const floorY = [];
    for (let i = 0; i < floors; i++) floorY.push(base + i * fh);

    // vãos por lado, convertidos de "relativo ao andar" para altura de mundo
    const byside = { n: [], s: [], w: [], e: [] };
    for (const op of (o.openings || [])) {
      const fy = base + (op.andar ?? op.floor ?? 0) * fh;
      (byside[op.lado || op.side] || byside.n).push({
        u0: op.u0, u1: op.u1, y0: fy + op.y0, y1: fy + op.y1, frame: op.frame,
      });
    }
    const wopt = (side) => ({ tint, trim, ribs: o.ribs || 0, plinth: o.plinth !== false, openings: byside[side] });

    this.wall(surface, x0, z0, x1, z0, base, wallH, t, wopt('n'));
    this.wall(surface, x0, z1, x1, z1, base, wallH, t, wopt('s'));
    this.wall(surface, x0, z0 + t, x0, z1 - t, base, wallH, t, wopt('w'));
    this.wall(surface, x1, z0 + t, x1, z1 - t, base, wallH, t, wopt('e'));

    // lajes intermediárias (o térreo é o próprio chão)
    const ins = t * 0.5 + 0.05;
    for (let i = 1; i < floors; i++) {
      const holes = (o.holes || []).filter((h) => (h.andar ?? h.floor ?? 1) === i).map((h) => h.rect);
      this.slab(o.slabSurface || 'concrete', x0 - ins, x1 + ins, z0 - ins, z1 + ins,
        base + i * fh, o.slabThick ?? 0.3, holes, { tint: o.slabTint || [0.92, 0.9, 0.87] });
    }

    let roofY = wallH;
    if (o.roof !== false) {
      const holes = (o.holes || []).filter((h) => (h.andar ?? h.floor) === floors).map((h) => h.rect);
      roofY = wallH + (o.roofThick ?? 0.35);
      this.roof(o.roofSurface || 'concrete', x0 - ins, x1 + ins, z0 - ins, z1 + ins, roofY, {
        thick: o.roofThick ?? 0.35,
        holes,
        overhang: o.overhang ?? 0,
        tint: o.roofTint || [0.9, 0.89, 0.86],
        parapeito: o.parapeito === false ? null : {
          h: o.parapeito?.h ?? 0.9,
          t: o.parapeito?.t ?? 0.3,
          gaps: o.parapeito?.gaps || [],
          over: (o.overhang ?? 0) * 0.5,
          surface: o.parapeito?.surface,
        },
      });
    }
    return { floorY, roofY, topY: roofY };
  }

  // ---------------------------------------------------------------------------
  // cercado
  // ---------------------------------------------------------------------------

  /**
   * Cerca de tela com montantes. `gaps` são intervalos [u0,u1] no eixo dominante
   * deixados abertos — é assim que se faz um PORTÃO: o vão fica sem painel e sem
   * colisor, e `gate:true` acrescenta as folhas e o batente.
   * Os painéis viram uma malha alfa única no flush.
   */
  fence(x0, z0, x1, z1, h, gaps = [], opt = EMPTY) {
    const horiz = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    const len = Math.hypot(x1 - x0, z1 - z0);
    const n = Math.max(1, Math.round(len / 2.4));
    for (let i = 0; i < n; i++) {
      const t0 = i / n, t1 = (i + 1) / n;
      const u0 = horiz ? lerp(x0, x1, t0) : lerp(z0, z1, t0);
      const u1 = horiz ? lerp(x0, x1, t1) : lerp(z0, z1, t1);
      if (gaps.some((g) => u1 > g[0] && u0 < g[1])) continue;
      const cx = lerp(x0, x1, (t0 + t1) * 0.5), cz = lerp(z0, z1, (t0 + t1) * 0.5);
      this.fencePanels.push([cx, cz, u1 - u0, h, horiz]);
      const ex = horiz ? Math.abs(u1 - u0) / 2 : 0.06;
      const ez = horiz ? 0.06 : Math.abs(u1 - u0) / 2;
      this.colliderBox(cx - ex, 0, cz - ez, cx + ex, h, cz + ez, 'metalPainted');
    }
    // montantes + travessa superior vivem no lote de metal
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      this.deco('gunMetal', lerp(x0, x1, t), h * 0.5 + 0.05, lerp(z0, z1, t), 0.1, h + 0.1, 0.1, { density: 2 });
    }
    this.deco('gunMetal', (x0 + x1) * 0.5, h - 0.02, (z0 + z1) * 0.5, horiz ? len : 0.06, 0.06, horiz ? 0.06 : len, { density: 2 });

    // portão: batentes grossos nas bordas do vão e uma folha entreaberta
    if (opt.gate) {
      for (const g of gaps) {
        const w = g[1] - g[0];
        for (const u of [g[0], g[1]]) {
          const px = horiz ? u : (x0 + x1) * 0.5;
          const pz = horiz ? (z0 + z1) * 0.5 : u;
          this.solid('gunMetal', px, (h + 0.35) * 0.5, pz, 0.18, h + 0.35, 0.18, { density: 2 });
        }
        const cx = horiz ? (g[0] + g[1]) * 0.5 : (x0 + x1) * 0.5;
        const cz = horiz ? (z0 + z1) * 0.5 : (g[0] + g[1]) * 0.5;
        const swing = opt.gateSwing ?? 0.6;
        // uma folha só, aberta: passagem livre e leitura imediata de "entrada"
        this.deco('metalPainted', cx - (horiz ? w * 0.25 : 0), h * 0.5, cz - (horiz ? 0 : w * 0.25),
          horiz ? w * 0.5 : 0.1, h * 0.94, horiz ? 0.1 : w * 0.5,
          { rotY: swing, density: 1.2, tint: [0.78, 0.75, 0.7] });
      }
    }
  }

  /** Contêiner marítimo: casco sólido, corrugação, castanhas de canto, porta. */
  container(x, z, yaw, height, opt = EMPTY) {
    const L = opt.length ?? 6.0, W = opt.width ?? 2.44;
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const tint = opt.tint || [0.85 + this.rng() * 0.3, 0.8, 0.75];
    this.solid('rust', x, height * 0.5, z, L, height, W, { rotY: yaw, density: 0.7, tint });
    for (let u = -L / 2 + 0.35; u < L / 2; u += 0.42) {
      this.deco('rust', x + c * u, height * 0.5, z - s * u, 0.12, height - 0.28, W + 0.1, { rotY: yaw, density: 1.6, tint });
    }
    for (const su of [-1, 1]) {
      for (const sv of [-1, 1]) {
        const ox = c * su * (L / 2 - 0.14) + s * sv * (W / 2 - 0.14);
        const oz = -s * su * (L / 2 - 0.14) + c * sv * (W / 2 - 0.14);
        this.deco('gunMetal', x + ox, 0.16, z + oz, 0.3, 0.32, 0.3, { rotY: yaw, density: 2 });
        this.deco('gunMetal', x + ox, height - 0.16, z + oz, 0.3, 0.32, 0.3, { rotY: yaw, density: 2 });
      }
    }
    this.deco('rust', x + c * (L / 2 + 0.03), height * 0.5, z - s * (L / 2 + 0.03), 0.06, height - 0.3, W - 0.2,
      { rotY: yaw, density: 1.2, tint: [0.75, 0.7, 0.66] });
    this.deco('gunMetal', x + c * (L / 2 + 0.08), height * 0.5, z - s * (L / 2 + 0.08), 0.08, height - 0.5, 0.09, { rotY: yaw, density: 2 });
    this.deco('rust', x, height + 0.04, z, L, 0.08, W, { rotY: yaw, density: 1.0, tint });
  }

  // ---------------------------------------------------------------------------
  // adereço
  // ---------------------------------------------------------------------------

  /** Pilha de caixotes: cada um sólido, encolhendo e girando um pouco por camada. */
  crateStack(x, z, size, n) {
    for (let i = 0; i < n; i++) {
      const s = size * (1 - i * 0.06);
      const y = i * size;
      const r = (this.rng() - 0.5) * 0.35;
      this.solid('woodPlank', x, y + s * 0.5, z, s, s, s, { rotY: r, density: 1.3, tint: [0.95, 0.9, 0.82] });
      for (const e of [-1, 1]) {
        this.deco('woodPlank', x, y + s * 0.5, z + e * (s * 0.5 + 0.02), s + 0.04, 0.1, 0.04, { rotY: r, density: 2, tint: [0.8, 0.74, 0.66] });
        this.deco('woodPlank', x + e * (s * 0.5 + 0.02), y + s * 0.5, z, 0.04, 0.1, s + 0.04, { rotY: r, density: 2, tint: [0.8, 0.74, 0.66] });
      }
    }
  }

  /**
   * Bloco baixo de concreto com capeamento — a cobertura dura do pátio.
   * `sy` é a ALTURA, e ela é o parâmetro que decide o jogo: 1,15 m cobre
   * agachado e deixa atirar de pé, 1,45 m cobre de pé. Consome 1 sorteio para
   * variar o tom, senão oito blocos iguais leem como oito cópias do mesmo bloco.
   */
  coverBlock(x, z, sx, sy, sz, opt = EMPTY) {
    const tone = 0.92 + this.rng() * 0.14;
    this.solid(opt.surface || 'concrete', x, sy * 0.5, z, sx, sy, sz,
      { tint: [tone, tone * 0.99, tone * 0.97], density: 0.6 });
    this.deco(opt.surface || 'concrete', x, sy + 0.06, z, sx + 0.16, 0.12, sz + 0.16,
      { tint: [0.86, 0.85, 0.82], density: 0.8 });
  }

  /** Floreira de concreto com terra dentro. Cobertura na altura da cintura. */
  planter(x, z, opt = EMPTY) {
    const w = opt.w ?? 1.7, h = opt.h ?? 0.84;
    this.solid('concrete', x, h * 0.5, z, w, h, w, { tint: [0.9, 0.88, 0.85], density: 0.9 });
    this.deco(opt.fill || 'sand', x, h + 0.03, z, w - 0.3, 0.08, w - 0.3, { tint: [0.8, 0.74, 0.6], density: 1.2 });
  }

  /**
   * Estante de armazém: montantes, longarinas e o estrado que elas carregam.
   * Os montantes e as longarinas são sólidos — é cobertura recortada, que é o
   * que faz um galpão jogar diferente de um pátio aberto.
   */
  rack({ x0, x1, z, depth = 2.4, height = 3.2, post = 0.16, levels = [1.05, 2.15] }) {
    const cx = (x0 + x1) * 0.5, span = (x1 - x0) + 0.2;
    for (const x of [x0, x1]) {
      for (const zz of [z, z + depth]) this.solid('gunMetal', x, height * 0.5, zz, post, height, post, { density: 2 });
    }
    for (const y of levels) {
      this.solid('gunMetal', cx, y, z, span, 0.12, 0.14, { density: 2 });
      this.solid('gunMetal', cx, y, z + depth, span, 0.12, 0.14, { density: 2 });
      this.deco('woodPlank', cx, y + 0.09, z + depth * 0.5, span - 0.1, 0.06, depth - 0.1,
        { density: 1.2, tint: [0.9, 0.86, 0.8] });
    }
  }

  /** Barraca de feira: bancada sólida, dois mastros e o pano por cima. */
  stall(x, z, yaw, opt = EMPTY) {
    const w = opt.w ?? 2.2, d = opt.d ?? 1.0;
    this.solid('woodPlank', x, 0.45, z, w, 0.9, d, { rotY: yaw, density: 1.4, tint: [0.92, 0.86, 0.78] });
    const pu = w * 0.5 - 0.1;                 // mastro recuado 10 cm da ponta da bancada
    for (const s of [-1, 1]) {
      this.deco('woodPlank', x + Math.cos(yaw) * s * pu, 1.1, z - Math.sin(yaw) * s * pu,
        0.1, 2.2, 0.1, { density: 2 });
    }
    const cu = w * 0.5 + 0.2, cv = d * 0.5 + 0.4;   // o pano avança sobre a bancada
    this.cloth([
      [x - cu, 2.3, z - cv], [x + cu, 2.3, z - cv],
      [x + cu, 2.15, z + cv], [x - cu, 2.15, z + cv],
    ], 0.18, opt.tint || [1.0, 0.72, 0.5]);
  }

  /** Chapa ondulada encostada na parede. Só enfeite: 6 cm não é cobertura. */
  leanSheet(x, z, yaw, opt = EMPTY) {
    this.deco('metalPainted', x, 1.05, z, 1.6, 2.2, 0.06, { rotY: yaw, density: 2.2, tint: [0.85, 0.78, 0.68] });
    this.deco('rust', x + 0.06, 1.0, z + 0.06, 1.4, 1.9, 0.05, { rotY: yaw, density: 2.0, tint: [0.9, 0.85, 0.8] });
  }

  /** Caixa d'água de telhado. Sólida: é a cobertura de quem sobe no telhado. */
  tank(x, y, z, opt = EMPTY) {
    const r = opt.radius ?? 0.85, h = opt.height ?? 1.3;
    const lid = opt.lid ?? 0.5;
    this.cylinder('rust', x, y + h * 0.5, z, r, h, { tint: [0.95, 0.9, 0.85], solid: true });
    this.deco('gunMetal', x, y + h + 0.06, z, lid, 0.12, lid, { density: 2 });
  }

  /** Exaustor de telhado: tubo + chapéu. */
  vent(x, y, z, opt = EMPTY) {
    const r = opt.radius ?? 0.28;
    this.cylinder('metalPainted', x, y, z, r + 0.06, 0.55, { rTop: r, seg: 10, tint: [0.9, 0.92, 0.94] });
    this.cone('metalPainted', x, y + 0.36, z, r + 0.14, 0.22, { seg: 10, tint: [0.85, 0.88, 0.9] });
  }

  /** Condensador de ar-condicionado de laje. Sólido — dá cobertura no telhado. */
  acUnit(x, y, z, opt = EMPTY) {
    const w = opt.w ?? 1.6, h = opt.h ?? 0.9, d = opt.d ?? 1.2;
    this.solid('metalPainted', x, y, z, w, h, d, { density: 1.2, tint: [0.85, 0.88, 0.9] });
    this.deco('gunMetal', x, y + h * 0.5 + 0.04, z, w - 0.4, 0.1, d - 0.3, { density: 2 });   // grelha do ventilador
    for (const s of [-1, 1]) {
      this.deco('gunMetal', x + s * (w * 0.5 - 0.25), y - (h * 0.5 - 0.02), z, 0.12, 0.16, d - 0.2, { density: 2 });
    }
  }

  /**
   * Parapeito de sacaria — FASE 1: só reserva o volume e o colisor. As sacas em
   * si são instanciadas depois, por `sandbags()`, porque instância é malha e
   * malha vem no fim. Chame na fase de geometria.
   */
  emplacement({ x, z, yaw = 0, len = 3.5, y = 0, rows = 3 }) {
    const e = { x, z, yaw, len, y, rows };
    this.emplacements.push(e);
    const c = Math.cos(yaw), s = Math.sin(yaw);
    this.colliderBox(
      x - Math.abs(c * len + s * 0.6) * 0.5, y, z - Math.abs(s * len + c * 0.6) * 0.5,
      x + Math.abs(c * len + s * 0.6) * 0.5, y + 1.05, z + Math.abs(s * len + c * 0.6) * 0.5,
      'fabric');
    return e;
  }

  /** Sacaria — FASE 2: drena os parapeitos reservados e emite uma instância só. */
  sandbags() {
    if (!this.emplacements.length) return;
    const rng = this.rng;
    const bagSrc = new THREE.SphereGeometry(0.5, 7, 4);
    const bagBatch = new Batch();
    bagBatch.addGeometry(bagSrc, new THREE.Matrix4().makeScale(1.1, 0.42, 0.68), { tint: [1, 1, 1], uvScale: 1.2 });
    bagSrc.dispose();
    const bagGeo = bagBatch.geometry();
    const places = [];
    for (const e of this.emplacements) {
      const rows = e.rows ?? 3, per = Math.max(2, Math.round(e.len / 0.62));
      for (let r = 0; r < rows; r++) {
        const inset = r * 0.06;
        for (let i = 0; i < per - r; i++) {
          const u = (-((per - r) - 1) / 2 + i) * 0.62;
          const c = Math.cos(e.yaw), s = Math.sin(e.yaw);
          places.push([
            e.x + c * u + s * inset, e.y + 0.19 + r * 0.34, e.z - s * u + c * inset,
            e.yaw + (rng() - 0.5) * 0.25,
          ]);
        }
      }
    }
    this.emplacements = [];
    this.instance(bagGeo, 'fabric', places.map(([x, y, z, r]) => [x, y, z, r, 1]), 'sandbags');
  }

  /** Geometria de caixote (fonte para instância). */
  _crateGeometry() {
    const crate = new Batch();
    crate.addBox(0, 0, 0, 1, 1, 1, { density: 1.3, tint: [0.96, 0.91, 0.83] });
    for (const e of [-1, 1]) {
      crate.addBox(0, 0, e * 0.52, 1.06, 0.11, 0.04, { density: 2, tint: [0.8, 0.74, 0.66] });
      crate.addBox(e * 0.52, 0, 0, 0.04, 0.11, 1.06, { density: 2, tint: [0.8, 0.74, 0.66] });
      crate.addBox(0, e * 0.42, 0, 1.06, 0.08, 1.06, { density: 2, tint: [0.84, 0.78, 0.7] });
    }
    return crate.geometry();
  }

  /**
   * Caixotes espalhados nos pontos dados, apoiados no que houver embaixo, com
   * chance de um segundo empilhado. São sólidos: viram cobertura de verdade.
   */
  crates(spots, opt = EMPTY) {
    const rng = this.rng;
    const stackChance = opt.stackChance ?? 0.35;
    const places = [];
    for (const [x, z] of spots) {
      const s = 0.75 + rng() * 0.5;
      const y = this.groundAt(x, z);
      places.push([x, y + s * 0.5, z, rng() * 3.14, s]);
      if (rng() < stackChance) places.push([x + (rng() - 0.5) * 0.2, y + s * 1.45, z + (rng() - 0.5) * 0.2, rng() * 3.14, s * 0.85]);
    }
    for (const p of places) {
      this.colliderBox(p[0] - p[4] * 0.62, p[1] - p[4] * 0.5, p[2] - p[4] * 0.62,
        p[0] + p[4] * 0.62, p[1] + p[4] * 0.5, p[2] + p[4] * 0.62, 'woodPlank');
    }
    this.instance(this._crateGeometry(), 'woodPlank', places, 'crates');
  }

  /** Paletes largados. Não são sólidos: 12 cm de altura só atrapalhariam. */
  pallets({ count = 16, area = [-31, 30, -31, 31], extra = [] } = {}) {
    const rng = this.rng;
    const pallet = new Batch();
    for (let i = 0; i < 6; i++) pallet.addBox(-0.5 + i * 0.2, 0.11, 0, 0.15, 0.03, 1.0, { density: 2, tint: [0.9, 0.84, 0.74] });
    for (const z of [-0.4, 0, 0.4]) pallet.addBox(0, 0.05, z, 1.1, 0.08, 0.12, { density: 2, tint: [0.82, 0.76, 0.68] });
    const places = [];
    const probe = new THREE.Vector3();
    for (let i = 0; i < count; i++) {
      const x = lerp(area[0], area[1], rng()), z = lerp(area[2], area[3], rng());
      probe.set(x, 0.4, z);
      if (this.isSolid(probe, 0.5)) continue;
      places.push([x, this.groundAt(x, z) + 0.02, z, rng() * 3.14, 0.9 + rng() * 0.25]);
    }
    for (const e of extra) places.push(e);
    this.instance(pallet.geometry(), 'woodPlank', places, 'pallets');
  }

  /** Tambor de 200 L. Sólido, porque é cobertura de pé. */
  barrel(x, z, opt = EMPTY) { this.drums([[x, z]], opt); }

  drums(spots, opt = EMPTY) {
    const rng = this.rng;
    const drum = new Batch();
    const dc = new THREE.CylinderGeometry(0.3, 0.3, 0.88, 14, 1);
    drum.addGeometry(dc, new THREE.Matrix4(), { uvScale: 2, tint: [1, 1, 1] });
    dc.dispose();
    for (const y of [-0.2, 0.2]) {
      const ring = new THREE.TorusGeometry(0.3, 0.035, 5, 14);
      ring.rotateX(Math.PI / 2);
      drum.addGeometry(ring, new THREE.Matrix4().makeTranslation(0, y, 0), { uvScale: 2, tint: [0.85, 0.8, 0.76] });
      ring.dispose();
    }
    const places = [];
    for (const [x, z] of spots) {
      const y = this.groundAt(x, z);
      places.push([x, y + 0.44, z, rng() * 3.14, 1]);
      this.colliderBox(x - 0.3, y, z - 0.3, x + 0.3, y + 0.88, z + 0.3, 'rust');
    }
    this.instance(drum.geometry(), 'rust', places, opt.name || 'drums');
  }

  /** Pilhas de pneu. Uma coluna de colisão por pilha. */
  tyres(stacks) {
    const rng = this.rng;
    const tyre = new THREE.TorusGeometry(0.34, 0.13, 6, 12);
    tyre.rotateX(Math.PI / 2);
    const b = new Batch();
    b.addGeometry(tyre, new THREE.Matrix4(), { uvScale: 3, tint: [0.3, 0.29, 0.29] });
    tyre.dispose();
    const places = [];
    for (const [x, z, n] of stacks) {
      const base = this.groundAt(x, z);
      for (let i = 0; i < n; i++) places.push([x + (rng() - 0.5) * 0.1, base + 0.14 + i * 0.26, z + (rng() - 0.5) * 0.1, rng() * 3.14, 1]);
      this.colliderBox(x - 0.45, base, z - 0.45, x + 0.45, base + n * 0.26, z + 0.45, 'rust');
    }
    this.instance(b.geometry(), 'gunMetal', places, 'tyres');
  }

  /**
   * Escombro: pedra irregular espalhada, adensada em torno dos focos (onde a
   * parede caiu). `hotspots` é [x, z, desvio]. Nada disso é sólido.
   */
  rubbleField({ count = 210, hotspots = [], limit = 33.5, radius = [5, 33], size = [0.35, 1.95], name = 'rubble', tint = [0.9, 0.88, 0.85] } = {}) {
    const rng = this.rng;
    const geo = new THREE.IcosahedronGeometry(0.22, 0);
    const places = [];
    for (let i = 0; i < count; i++) {
      let x, z;
      if (i % 3 === 0 && hotspots.length) {
        const h = hotspots[i % hotspots.length];
        x = h[0] + rng.gauss() * h[2]; z = h[1] + rng.gauss() * h[2];
      } else {
        const a = rng() * Math.PI * 2, r = radius[0] + rng() * (radius[1] - radius[0]);
        x = Math.cos(a) * r; z = Math.sin(a) * r;
      }
      if (Math.abs(x) > limit || Math.abs(z) > limit) continue;
      const s = size[0] + rng() * (size[1] - size[0]);
      places.push([x, this.groundAt(x, z) + 0.05 * s, z, rng() * 3.14, s]);
    }
    this.instance(geo, 'concrete', places, name, tint);
  }

  /** Cacos de tijolo, só em volta dos focos de destruição. */
  bricks({ count = 120, hotspots = [], spread = 1.2, limit = 33.5, tint = [0.78, 0.5, 0.38] } = {}) {
    const rng = this.rng;
    const geo = new THREE.BoxGeometry(0.22, 0.1, 0.11);
    const places = [];
    for (let i = 0; i < count; i++) {
      const h = hotspots[i % hotspots.length];
      const x = h[0] + rng.gauss() * h[2] * spread, z = h[1] + rng.gauss() * h[2] * spread;
      if (Math.abs(x) > limit || Math.abs(z) > limit) continue;
      places.push([x, this.groundAt(x, z) + 0.05, z, rng() * 3.14, 0.8 + rng() * 0.7]);
    }
    this.instance(geo, 'concrete', places, 'bricks', tint);
  }

  /** Papel e lixo leve rente ao chão. */
  litter({ count = 130, radius = [3, 32], limit = 33, tint = [1.6, 1.55, 1.45] } = {}) {
    const rng = this.rng;
    const geo = new THREE.PlaneGeometry(0.21, 0.28);
    geo.rotateX(-Math.PI / 2);
    const places = [];
    for (let i = 0; i < count; i++) {
      const a = rng() * Math.PI * 2, r = radius[0] + rng() * (radius[1] - radius[0]);
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (Math.abs(x) > limit || Math.abs(z) > limit) continue;
      places.push([x, this.groundAt(x, z) + 0.022, z, rng() * 3.14, 0.8 + rng() * 0.8]);
    }
    this.instance(geo, 'fabricDouble', places, 'paper', tint);
  }

  /** Cabo suspenso com catenária. Vai para o lote de ferro. */
  cable(a, b, sag, radius = 0.035) {
    const pts = [];
    const N = 8;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      pts.push(new THREE.Vector3(
        lerp(a[0], b[0], t),
        lerp(a[1], b[1], t) - Math.sin(t * Math.PI) * sag,
        lerp(a[2], b[2], t),
      ));
    }
    const curve = new THREE.CatmullRomCurve3(pts);
    const geo = new THREE.TubeGeometry(curve, 12, radius, 4, false);
    this.geo('gunMetal', geo, new THREE.Matrix4(), { tint: [0.35, 0.34, 0.33], uvScale: 6, vScale: 1 });
    geo.dispose();
  }

  /**
   * Poste com fio: mastro sólido, braço e cabos partindo do topo para os
   * destinos dados. `wires` é uma lista de [x, y, z] (ponta oposta do cabo).
   */
  pole(x, z, h, { base = 0, surface = 'woodPlank', wires = [], sag = 0.9, arm = 0.9, lamp = false, tint } = {}) {
    const topY = base + h;
    this.solid(surface, x, base + h * 0.5, z, 0.22, h, 0.22, { density: 1.4, tint: tint || [0.86, 0.8, 0.7] });
    if (arm > 0) this.deco('gunMetal', x, topY - 0.35, z, arm, 0.08, 0.1, { density: 2 });
    for (const w of wires) this.cable([x, topY - 0.4, z], w, sag, 0.03);
    if (lamp) {
      this.deco('gunMetal', x + 0.4, topY - 0.15, z, 0.8, 0.08, 0.1, { density: 2 });
      this.deco('metalPainted', x + 0.78, topY - 0.28, z, 0.34, 0.16, 0.24, { density: 1.6, tint: [0.8, 0.82, 0.8] });
    }
    return topY;
  }

  /** Mastro de antena estaiado. */
  mast(x, y, z, h) {
    this.deco('gunMetal', x, y + h * 0.5, z, 0.1, h, 0.1, { density: 2 });
    for (let i = 1; i < 4; i++) {
      this.deco('gunMetal', x, y + h * (i / 4), z, 0.42, 0.05, 0.42, { density: 2, rotY: i * 0.4 });
    }
    for (let i = 0; i < 3; i++) {
      const a = i * 2.09;
      this.cable([x, y + h * 0.82, z], [x + Math.cos(a) * 2.6, y + 0.1, z + Math.sin(a) * 2.6], 0.25, 0.02);
    }
    this.deco('gunMetal', x, y + h + 0.25, z, 0.05, 0.5, 0.05, { density: 2 });
  }

  /** Antena parabólica. */
  dish(x, y, z, yaw) {
    const geo = new THREE.SphereGeometry(0.72, 14, 8, 0, Math.PI * 2, 0, Math.PI * 0.42);
    const m = new THREE.Matrix4().compose(
      new THREE.Vector3(x, y + 0.75, z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(-1.0, yaw, 0, 'YXZ')),
      new THREE.Vector3(1, 1, 1),
    );
    this.geo('metalPainted', geo, m, { tint: [1.05, 1.03, 1.0], uvScale: 2 });
    geo.dispose();
    this.deco('gunMetal', x, y + 0.36, z, 0.12, 0.72, 0.12, { density: 2 });
    this.deco('gunMetal', x + Math.cos(yaw) * 0.42, y + 0.9, z + Math.sin(yaw) * 0.42, 0.14, 0.14, 0.14, { density: 2 });
  }

  /** Pano em barriga, 5x3 quads para nunca ler como papelão dobrado. */
  cloth(corners, sag, tint) {
    const b = this.batch('fabricDouble');
    const NU = 5, NV = 3;
    const at = (u, v) => {
      const a = corners[0], bb = corners[1], c = corners[2], d = corners[3];
      const x = lerp(lerp(a[0], bb[0], u), lerp(d[0], c[0], u), v);
      const y = lerp(lerp(a[1], bb[1], u), lerp(d[1], c[1], u), v);
      const z = lerp(lerp(a[2], bb[2], u), lerp(d[2], c[2], u), v);
      return [x, y - Math.sin(u * Math.PI) * sag * (0.35 + v * 0.65), z];
    };
    for (let i = 0; i < NU; i++) {
      for (let j = 0; j < NV; j++) {
        b.addQuad(at(i / NU, j / NV), at((i + 1) / NU, j / NV), at((i + 1) / NU, (j + 1) / NV), at(i / NU, (j + 1) / NV),
          { tint, density: 1.6 });
      }
    }
  }

  /**
   * Toldo: pano caído da parede para fora, com dois montantes de apoio sólidos.
   * `eixo:'z'` cai no sentido -Z (fachada virada ao norte), `'x'` no sentido -X.
   */
  awning({ x, z, w = 4.2, depth = 2.1, yHigh = 3.28, yLow = 2.62, sag = 0.22, tint = [1.0, 0.7, 0.45], posts = true, eixo = 'z' }) {
    const hw = w * 0.5;
    if (eixo === 'z') {
      this.cloth([
        [x - hw, yHigh, z], [x + hw, yHigh, z], [x + hw, yLow, z - depth], [x - hw, yLow, z - depth],
      ], sag, tint);
      if (posts) {
        for (const s of [-1, 1]) {
          this.solid('woodPlank', x + s * hw, (yLow - 0.02) * 0.5, z - depth, 0.12, yLow - 0.02, 0.12, { density: 2, tint: [0.92, 0.86, 0.78] });
        }
      }
    } else {
      this.cloth([
        [x, yHigh, z - hw], [x, yHigh, z + hw], [x - depth, yLow, z + hw], [x - depth, yLow, z - hw],
      ], sag, tint);
      if (posts) {
        for (const s of [-1, 1]) {
          this.solid('woodPlank', x - depth, (yLow - 0.02) * 0.5, z + s * hw, 0.12, yLow - 0.02, 0.12, { density: 2, tint: [0.92, 0.86, 0.78] });
        }
      }
    }
  }

  /** Varal: o fio mais os panos pendurados, cada pano uma malha que balança. */
  laundry(x0, y, z0, x1, y1, z1, n) {
    this.cable([x0, y, z0], [x1, y1, z1], 0.18, 0.018);
    const mat = this.mat('fabricDouble');
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const px = lerp(x0, x1, t), pz = lerp(z0, z1, t);
      const py = lerp(y, y1, t) - Math.sin(t * Math.PI) * 0.18;
      const w = 0.7 + this.rng() * 0.4, h = 0.9 + this.rng() * 0.6;
      const b = new Batch();
      const tint = [0.9 + this.rng() * 0.3, 0.85 + this.rng() * 0.2, 0.8];
      for (let j = 0; j < 3; j++) {
        const a0 = -w / 2 + (w / 3) * j, a1 = -w / 2 + (w / 3) * (j + 1);
        const zb = Math.sin((j + 0.5) * 1.7) * 0.06;
        b.addQuad([a0, 0, 0], [a1, 0, 0], [a1, -h, zb], [a0, -h, zb], { tint, density: 1.4 });
      }
      this.meshFor(b.geometry(), mat, {
        position: [px, py, pz],
        surface: 'fabric',
        sway: { phase: this.rng() * 6.28, amp: 0.06 + this.rng() * 0.05 },
      });
    }
  }

  /** Veículo destruído: casco, cabine sem teto, pneus queimados, marca no chão. */
  wreck(x, z, yaw, opt = EMPTY) {
    const burnt = opt.tint || [0.22, 0.2, 0.2];
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const at = (u, v) => [x + c * u + s * v, z - s * u + c * v];
    let p = at(0, 0);
    this.solid('gunMetal', p[0], 0.72, p[1], 4.9, 0.7, 2.1, { rotY: yaw, density: 1.1, tint: burnt });
    p = at(-1.5, 0);
    this.solid('gunMetal', p[0], 1.55, p[1], 1.9, 1.1, 2.0, { rotY: yaw, density: 1.2, tint: burnt });
    for (const [u, v] of [[-2.3, -0.9], [-2.3, 0.9], [-0.7, -0.9], [-0.7, 0.9]]) {
      p = at(u, v);
      this.deco('gunMetal', p[0], 2.2, p[1], 0.12, 1.2, 0.12, { rotY: yaw, density: 2, tint: burnt });
    }
    p = at(-1.5, 0);
    this.deco('gunMetal', p[0], 2.8, p[1], 1.9, 0.1, 1.9, { rotY: yaw + 0.06, density: 1.4, tint: [0.18, 0.17, 0.17] });
    p = at(1.2, -1.0);
    this.deco('rust', p[0], 1.35, p[1], 3.2, 0.6, 0.1, { rotY: yaw, density: 1.4, tint: [0.5, 0.4, 0.34] });
    p = at(1.2, 1.15);
    this.deco('rust', p[0], 1.1, p[1], 3.2, 0.6, 0.1, { rotY: yaw, rotX: 0, density: 1.4, tint: [0.5, 0.4, 0.34] });
    const wheel = new THREE.CylinderGeometry(0.44, 0.44, 0.32, 10, 1);
    wheel.rotateX(Math.PI / 2);
    const b = this.batch('gunMetal');
    for (const [u, v] of [[-1.6, -1.05], [-1.6, 1.05], [1.5, -1.05], [1.5, 1.05]]) {
      p = at(u, v);
      const m = new THREE.Matrix4().compose(
        new THREE.Vector3(p[0], 0.4, p[1]),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw, 0)),
        new THREE.Vector3(1, 1, 1),
      );
      b.addGeometry(wheel, m, { tint: [0.16, 0.15, 0.15], uvScale: 2 });
    }
    wheel.dispose();
    this.deco(opt.scorch || 'asphalt', x, 0.035, z, 7.0, 0.02, 4.4, { rotY: yaw, density: 0.5, tint: [0.35, 0.33, 0.32] });
  }

  // ---------------------------------------------------------------------------
  // materialização — chamada pelo Level, não pelo mapa
  // ---------------------------------------------------------------------------

  /** Textura procedural de tela de alambrado (canvas; sem arquivo binário). */
  chainLinkTexture() {
    try {
      const size = 128;
      const c = document.createElement('canvas');
      c.width = c.height = size;
      const g = c.getContext('2d');
      g.fillStyle = '#000'; g.fillRect(0, 0, size, size);
      g.strokeStyle = '#fff'; g.lineWidth = size * 0.06; g.lineCap = 'square';
      const step = size / 4;
      for (let i = -4; i <= 8; i++) {
        g.beginPath(); g.moveTo(i * step, 0); g.lineTo(i * step + size, size); g.stroke();
        g.beginPath(); g.moveTo(i * step, size); g.lineTo(i * step + size, 0); g.stroke();
      }
      const tex = new THREE.CanvasTexture(c);
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(3, 3);
      tex.anisotropy = 4;
      return tex;
    } catch (e) {
      return null;
    }
  }

  /** Malha única com todos os painéis de tela acumulados. */
  buildFenceMesh() {
    const panels = this.fencePanels;
    if (!panels || !panels.length) return null;
    const b = new Batch();
    for (const [cx, cz, len, h, horiz] of panels) {
      const a = [cx - (horiz ? len / 2 : 0), h, cz - (horiz ? 0 : len / 2)];
      const c = [cx + (horiz ? len / 2 : 0), h, cz + (horiz ? 0 : len / 2)];
      b.addQuad(a, c, [c[0], 0.02, c[2]], [a[0], 0.02, a[2]], { density: 1, tint: [1, 1, 1] });
    }
    let mat = this.mats.get('chainlink');
    if (!mat) {
      mat = new THREE.MeshStandardMaterial({
        color: 0x9aa0a2, roughness: 0.55, metalness: 0.85,
        side: THREE.DoubleSide, alphaTest: 0.45, vertexColors: true,
      });
      const tex = this.chainLinkTexture();
      if (tex) { mat.alphaMap = tex; mat.map = tex; }
      this.mats.set('chainlink', mat);
    }
    const mesh = new THREE.Mesh(b.geometry(), mat);
    mesh.name = 'level:chainlink';
    mesh.castShadow = false;   // VSM sobre malha com alpha de 45% é ruído, não sombra
    mesh.receiveShadow = true;
    mesh.userData.surface = 'metalPainted';
    this.fencePanels = [];
    return { mesh, triangles: b.triangles };
  }
}
